import { useMemo } from 'react';

import {
  ACTIVITIES_BASE_HEIGHT_SMALL,
  SUGGESTED_ANSWER_EXAMPLE,
  SUGGESTED_ANSWER_VARY,
} from 'global/constants';
import { ComponentDraw } from 'approot/activity-builder/components/component.constants';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import {
  ClassroomAnswerInstruction,
  ClassroomSuggestedAnswer,
} from '../classroom-answer.style';
import {
  useGetImageAnswer,
  useGetRichtextAnswer,
} from 'approot/activity-builder/components/suggested-answers/suggested-answer.hooks';
import { BadgeSmallStyled } from 'approot/shared/badge/badge.style';
import { ImageStatic } from 'approot/shared/images/images';
import { ResponseFieldReadonly } from 'approot/activity-builder/components/response/response-field-readonly.style';
import { SUGGESTED_ANSWER_BASE_RICHTEXT } from 'approot/activity-builder/components/suggested-answers/suggested-answer.constants';
import { getRichtextValue } from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import { getAnswerTypeLabel } from 'approot/activity-builder/components/suggested-answers/logic/get-answer-type-label';
import { DrawResponseSuggestedContainer } from './draw-response-suggested-answer.style';
import { isRichtextEmpty } from 'approot/shared/richtext/richtext.utils';

type Props = {
  component: Omit<ComponentDraw, '__typename'>;
};

const DrawResponseSuggestedAnswer = ({
  component: {
    id,
    properties,
    subActivityProperties,
    suggestedAnswerProperties,
  },
}: Props) => {
  const answerType =
    suggestedAnswerProperties?.suggestedAnswerType || SUGGESTED_ANSWER_EXAMPLE;

  const paddingTop = useMemo(
    () =>
      (properties.dimensionData.height / properties.dimensionData.width) * 100,
    [properties.dimensionData]
  );

  const richtextAnswer = useGetRichtextAnswer({
    componentId: id,
    suggestedAnswerProperties,
  });

  const imageAnswer = useGetImageAnswer({
    componentId: id,
    suggestedAnswerProperties,
  });

  return (
    <ClassroomAnswerInstruction>
      <SubActivityView subActivityDetails={subActivityProperties}>
        {answerType === SUGGESTED_ANSWER_VARY ? (
          <ClassroomSuggestedAnswer>
            <ResponseFieldReadonly
              $numberOflines={3}
              $baseHeight={ACTIVITIES_BASE_HEIGHT_SMALL}
            />
            <BadgeSmallStyled>
              {getAnswerTypeLabel(answerType)}
            </BadgeSmallStyled>
          </ClassroomSuggestedAnswer>
        ) : (
          <>
            {imageAnswer?.original && (
              <ClassroomSuggestedAnswer>
                <DrawResponseSuggestedContainer
                  style={{
                    paddingTop: `${paddingTop}%`,
                  }}
                >
                  <ImageStatic
                    imageUrl={imageAnswer.original}
                    alt={imageAnswer.alt || ''}
                  />
                  <BadgeSmallStyled>
                    {getAnswerTypeLabel(answerType)}
                  </BadgeSmallStyled>
                </DrawResponseSuggestedContainer>
              </ClassroomSuggestedAnswer>
            )}
            {!isRichtextEmpty(richtextAnswer) && (
              <ClassroomSuggestedAnswer>
                <BadgeSmallStyled>
                  {getAnswerTypeLabel(answerType)}
                </BadgeSmallStyled>
                <ResponseFieldReadonly
                  $numberOflines={1}
                  $baseHeight={ACTIVITIES_BASE_HEIGHT_SMALL}
                  dangerouslySetInnerHTML={{
                    __html: getRichtextValue(
                      richtextAnswer,
                      SUGGESTED_ANSWER_BASE_RICHTEXT
                    ),
                  }}
                />
              </ClassroomSuggestedAnswer>
            )}
          </>
        )}
      </SubActivityView>
    </ClassroomAnswerInstruction>
  );
};

export default DrawResponseSuggestedAnswer;
